import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiUrlConstant } from "@constants/apiUrls";
import { verticalConfig } from "@config/verticalEnv";
import type { EvaluationResult, AttemptStatus } from "@constants/evaluationConstants";

// ============================================================================
// Type Definitions
// ============================================================================

export interface EvaluationGroup {
  id: string;
  name: string;
}

export interface GetEvaluationGroupsResponse {
  exam_evaluation_groups: EvaluationGroup[];
}

export interface PreviousEvaluationResult {
  awarded_marks: number;
  feedbacks: string[];
  evaluation_result: EvaluationResult;
  attempt_status: AttemptStatus;
}

export interface CurrentEvaluation {
  awarded_marks: number;
  feedbacks: string[];
  attempt_status: AttemptStatus;
}

export interface Question {
  question_id: string;
  question_number: number;
  min_marks: number;
  max_marks: number;
  rubrics: string;
  previous_evaluation_result: PreviousEvaluationResult | null;
  current_evaluation: CurrentEvaluation | null;
  student_comment: string[] | null;
}

export interface EvaluationTask {
  id: string;
  assignmentId: string;
  is_challenge: boolean;
  submission_url: string;
  evaluation_group_id: string;
  evaluation_phase_id:string;
  questions: Question[];
}

export interface SaveEvaluationRequest {
  question_id: string;
  awarded_marks: number;
  feedbacks: string[];
  attempt_status: AttemptStatus;
}

export interface SaveEvaluationResponse {
  message: string;
}

export interface SubmitEvaluationResponse {
  message: string;
}

export interface UnassignEvaluationResponse {
  message: string;
}

export interface ApiErrorResponse {
  detail:
    | string
    | Array<{
        loc: (string | number)[];
        msg: string;
        type: string;
      }>;
}

// ============================================================================
// V3 Evaluation Task Type Definitions
// ============================================================================

export interface EvaluatorPhase {
  id: string;
  label: string;
}

export interface GetEvaluatorPhasesResponse {
  evaluation_phases: EvaluatorPhase[];
}

export interface ExamSubmission {
  submission_by: number;
  submission_url: string;
  exam_id: string;
}

export interface QuestionEvaluationResult {
  awarded_marks: number;
  feedbacks: string[];
  attempt_status: AttemptStatus;
  evaluation_result: EvaluationResult;
}

export interface EvaluationTaskQuestionV3 {
  question_id: string;
  question_number: number;
  min_marks: number;
  max_marks: number;
  rubrics: string;
  previous_evaluation_result: QuestionEvaluationResult | null;
  current_evaluation: QuestionEvaluationResult | null;
  student_comment: string[] | null;
}

export interface EvaluationTaskV3 {
  id: string;
  assignment_id?: string;
  evaluation_group_id: string;
  evaluation_phase_id: string;
  exam_submission: ExamSubmission;
  submission_url?: string;
  submission_by?: number;
  questions: EvaluationTaskQuestionV3[];
}

function getAccessToken(): string {
  const token = localStorage.getItem("accessToken");
  if (!token) {
    throw new Error("Access token not found in localStorage");
  }
  return token;
}

function createApiInstance() {
  const accessToken = getAccessToken();
  return axios.create({
    baseURL: ApiUrlConstant.semServiceUrl,
    headers: {
      "Content-Type": "application/json",
      "product-id": "1000",
      "tenant-id": String(verticalConfig.gatewayConfiguration.tenantId),
      platform: "web",
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get Evaluation Groups to Evaluate
 * GET api/v2/evaluation-group/all?challenge=<boolean>
 */
export async function getEvaluationGroups(
  challenge?: boolean
): Promise<any> {
  try {
    const api = createApiInstance();
    const params = challenge === true ? { challenge: "true" } : {};
    const response = await api.get("/api/v2/evaluation-group/all", { params });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching evaluation groups:", error);
    throw (
      error?.response?.data ||
      error?.message ||
      new Error("Failed to fetch evaluation groups")
    );
  }
}

/**
 * Assign and get an evaluation task
 * GET /v2/evaluation-task/assign/{evaluation-group-id}
 */
export async function assignAndGetEvaluationTask(
  evaluationGroupId?: string
): Promise<EvaluationTask> {
  try {
    const api = createApiInstance();
    const url = evaluationGroupId
      ? `/api/v2/evaluation-task/assign/${evaluationGroupId}`
      : "/api/v2/evaluation-task/assign";
    const response = await api.get(url);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching evaluation tasks for group:", error);
    throw (
      error?.response?.data ||
      error?.message ||
      new Error("Failed to fetch evaluation tasks")
    );
  }
}

/**
 * Get Assigned Evaluation Task
 * GET /v2/evaluation-task/assigned
 */
export async function getAssignedEvaluationTask(): Promise<EvaluationTask> {
  try {
    const api = createApiInstance();
    const response = await api.get("/api/v2/evaluation-task/assigned");
    return response.data;
  } catch (error: any) {
    console.error("Error fetching assigned evaluation task:", error);
    throw (
      error?.response?.data ||
      error?.message ||
      new Error("Failed to fetch assigned evaluation task")
    );
  }
}

/**
 * Save Evaluation for a Task
 * POST /v2/evaluation-task/{assignment_id}/save-evaluation
 */
export async function saveEvaluation(
  assignmentId: string,
  data: SaveEvaluationRequest
): Promise<SaveEvaluationResponse> {
  try {
    const api = createApiInstance();
    const response = await api.post(
      `/api/v2/evaluation-task/${assignmentId}/save-evaluation`,
      data
    );
    return response.data;
  } catch (error: any) {
    console.error("Error saving evaluation:", error);
    throw (
      error?.response?.data ||
      error?.message ||
      new Error("Failed to save evaluation")
    );
  }
}

/**
 * Submit Evaluation for a Task
 * GET v2/evaluation-task/{assignment_id}/submit-evaluation
 */
export async function submitEvaluation(
  assignmentId: string
): Promise<SubmitEvaluationResponse> {
  try {
    const api = createApiInstance();
    const response = await api.get(
      `/api/v2/evaluation-task/${assignmentId}/submit-evaluation`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error submitting evaluation:", error);
    throw (
      error?.response?.data ||
      error?.message ||
      new Error("Failed to submit evaluation")
    );
  }
}

/**
 * Unassign Evaluation Task
 * GET /v2/evaluation-task/{assignment_id}/unassign
 */
export async function unassignEvaluationTask(
  assignmentId: string
): Promise<UnassignEvaluationResponse> {
  try {
    const api = createApiInstance();
    const response = await api.get(
      `/api/v2/evaluation-task/${assignmentId}/unassign`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error unassigning evaluation task:", error);
    throw (
      error?.response?.data ||
      error?.message ||
      new Error("Failed to unassign evaluation task")
    );
  }
}

/**
 * Get evaluator phases for an evaluation group
 * GET /api/v2/evaluation-group/{evaluation-group-id}/evaluator-phases
 */
export async function getEvaluatorPhases(
  evaluationGroupId: string
): Promise<GetEvaluatorPhasesResponse> {
  try {
    const api = createApiInstance();
    const response = await api.get(
      `/api/v2/evaluation-group/${evaluationGroupId}/evaluator-phases`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching evaluator phases:", error);
    throw (
      error?.response?.data ||
      error?.message ||
      new Error("Failed to fetch evaluator phases")
    );
  }
}

/**
 * Assign evaluation task by phase
 * POST /api/v3/evalution-task/assign/{evaluation-phase-id}
 */
export async function assignEvaluationTaskByPhase(
  evaluationPhaseId: string
): Promise<EvaluationTaskV3 | null> {
  try {
    const api = createApiInstance();
    const response = await api.post<EvaluationTaskV3>(
      `/api/v3/evaluation-task/assign/${evaluationPhaseId}`,
      {}
    );
    return normalizeAssignedTaskV3Response(response.data);
  } catch (error: any) {
    console.error("Error assigning evaluation task by phase:", error);
    throw (
      error?.response?.data ||
      error?.message ||
      new Error("Failed to assign evaluation task")
    );
  }
}

function normalizeAssignedTaskV3Response(
  data: EvaluationTaskV3 | null | undefined
): EvaluationTaskV3 | null {
  if (!data) return null;
  if (!data.id && !data.assignment_id) return null;
  return data;
}

/**
 * Get assigned evaluation task (v3)
 * GET /api/v3/evaluation-task/assigned
 */
export async function getAssignedEvaluationTaskV3(): Promise<EvaluationTaskV3 | null> {
  try {
    const api = createApiInstance();
    const response = await api.get<EvaluationTaskV3 | null>(
      "/api/v3/evaluation-task/assigned"
    );
    return normalizeAssignedTaskV3Response(response.data);
  } catch (error: any) {
    console.error("Error fetching assigned evaluation task (v3):", error);
    throw (
      error?.response?.data ||
      error?.message ||
      new Error("Failed to fetch assigned evaluation task")
    );
  }
}

// ============================================================================
// TanStack Query Hooks
// ============================================================================

/**
 * Query key factory for evaluation-related queries
 */
export const evaluationKeys = {
  all: ["evaluation"] as const,
  groups: (challenge?: boolean) =>
    [...evaluationKeys.all, "groups", challenge] as const,
  task: (groupId?: string) => [...evaluationKeys.all, "task", groupId] as const,
  taskByPhase: (phaseId?: string) =>
    [...evaluationKeys.all, "taskByPhase", phaseId] as const,
  assigned: () => [...evaluationKeys.all, "assigned"] as const,
  evaluatorPhases: (evaluationGroupId: string) =>
    [...evaluationKeys.all, "evaluatorPhases", evaluationGroupId] as const,
  assignedV3: () => [...evaluationKeys.all, "assignedV3"] as const,
};

/**
 * Hook to get evaluation groups
 * @param challenge - Optional boolean to filter challenge groups
 */
export function useGetEvaluationGroups(
  challenge?: boolean,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: evaluationKeys.groups(challenge),
    queryFn: () => getEvaluationGroups(challenge),
  });
}

/**
 * Hook to assign and get an evaluation task
 * @param evaluationGroupId - Optional UUID of the evaluation group
 * @param enabled - Whether the query should run (default: true)
 */
export function useAssignAndGetEvaluationTask(
  evaluationGroupId?: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: evaluationKeys.task(evaluationGroupId),
    queryFn: () => assignAndGetEvaluationTask(evaluationGroupId),
    enabled,
  });
}

/**
 * Hook to assign and get an evaluation task by phase (v3)
 * @param evaluationPhaseId - UUID of the evaluation phase
 * @param enabled - Whether the query should run (default: true)
 */
export function useAssignAndGetEvaluationTaskByPhase(
  evaluationPhaseId?: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: evaluationKeys.taskByPhase(evaluationPhaseId),
    queryFn: () => assignEvaluationTaskByPhase(evaluationPhaseId!),
    enabled: enabled && Boolean(evaluationPhaseId),
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/**
 * Hook to get assigned evaluation task
 * @param enabled - Whether the query should run (default: true)
 */
export function useGetAssignedEvaluationTask(enabled: boolean = true) {
  return useQuery({
    queryKey: evaluationKeys.assigned(),
    queryFn: () => getAssignedEvaluationTask(),
    enabled,
  });
}

/**
 * Hook to save evaluation for a question
 */
export function useSaveEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      assignmentId,
      data,
    }: {
      assignmentId: string;
      data: SaveEvaluationRequest;
    }) => saveEvaluation(assignmentId, data),
    onSuccess: (_, variables) => {
      const { data } = variables;

      const updateEvaluationTask = (oldTask: EvaluationTask | undefined) => {
        if (!oldTask) return oldTask;
        return {
          ...oldTask,
          questions: oldTask.questions.map((q) =>
            q.question_id === data.question_id
              ? {
                  ...q,
                  current_evaluation: {
                    awarded_marks: data.awarded_marks,
                    feedbacks: data.feedbacks,
                    attempt_status: data.attempt_status,
                  },
                }
              : q
          ),
        };
      };

      const updateEvaluationTaskV3 = (
        oldTask: EvaluationTaskV3 | null | undefined
      ) => {
        if (!oldTask) return oldTask;
        return {
          ...oldTask,
          questions: oldTask.questions.map((q) => {
            if (q.question_id !== data.question_id) return q;
            const evaluationResult =
              q.current_evaluation?.evaluation_result ??
              q.previous_evaluation_result?.evaluation_result ??
              "correct";
            return {
              ...q,
              current_evaluation: {
                awarded_marks: data.awarded_marks,
                feedbacks: data.feedbacks,
                attempt_status: data.attempt_status,
                evaluation_result: evaluationResult,
              },
            };
          }),
        };
      };

      queryClient.setQueriesData(
        { queryKey: ["evaluation", "task"] },
        updateEvaluationTask
      );
      queryClient.setQueryData(evaluationKeys.assigned(), updateEvaluationTask);
      queryClient.setQueryData(
        evaluationKeys.assignedV3(),
        updateEvaluationTaskV3
      );
    },
  });
}

/**
 * Hook to submit evaluation task
 */
export function useSubmitEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assignmentId: string) => submitEvaluation(assignmentId),
    onSuccess: () => {
      // Invalidate relevant queries after submission
      queryClient.invalidateQueries({ queryKey: evaluationKeys.all });
    },
  });
}

/**
 * Hook to unassign evaluation task
 */
export function useUnassignEvaluationTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assignmentId: string) => unassignEvaluationTask(assignmentId),
    onSuccess: () => {
      // Invalidate relevant queries after unassignment
      queryClient.invalidateQueries({ queryKey: evaluationKeys.all });
    },
  });
}

/**
 * Hook to get evaluator eligible phases for an evaluation group
 * @param evaluationGroupId - UUID of the evaluation group
 * @param enabled - Whether the query should run (default: true)
 */
export function useGetEvaluatorPhases(
  evaluationGroupId: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: evaluationKeys.evaluatorPhases(evaluationGroupId),
    queryFn: () => getEvaluatorPhases(evaluationGroupId),
    enabled: enabled && Boolean(evaluationGroupId),
  });
}

/**
 * Hook to assign an evaluation task by phase (v3)
 */
export function useAssignEvaluationTaskByPhase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (evaluationPhaseId: string) =>
      assignEvaluationTaskByPhase(evaluationPhaseId),
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(evaluationKeys.assignedV3(), data);
      }
    },
  });
}

/**
 * Hook to get assigned evaluation task (v3)
 * @param enabled - Whether the query should run (default: true)
 */
export function useGetAssignedEvaluationTaskV3(enabled: boolean = true) {
  return useQuery({
    queryKey: evaluationKeys.assignedV3(),
    queryFn: () => getAssignedEvaluationTaskV3(),
    enabled,
    staleTime: 60_000,
    refetchOnMount: true,
  });
}
